import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  CONSENT_ACCEPTED,
  CONSENT_DECLINED,
  getStoredConsent,
  initAnalytics,
  isProductionEnvironment,
  setStoredConsent,
  trackPageView,
} from '@/lib/analytics';

/**
 * GDPR-compliant cookie consent banner.
 * Only appears in production and only when the visitor has not yet chosen.
 * Loads Google Analytics (gtag.js) if — and only if — the visitor accepts.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isProductionEnvironment()) return;
    const stored = getStoredConsent();
    if (stored === CONSENT_ACCEPTED) {
      initAnalytics();
      // Fire an initial page_view for the current route.
      trackPageView();
      return;
    }
    if (stored === CONSENT_DECLINED) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    setStoredConsent(CONSENT_ACCEPTED);
    initAnalytics();
    trackPageView();
    setVisible(false);
  };

  const handleDecline = () => {
    setStoredConsent(CONSENT_DECLINED);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 z-[200] sm:inset-x-6 sm:bottom-6"
    >
      <div
        className="mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl border border-moj-gold/40 bg-moj-purple-deep/95 p-5 text-moj-cream shadow-2xl backdrop-blur-md sm:flex-row sm:items-center sm:gap-6 sm:p-6"
        style={{ boxShadow: '0 20px 45px -10px rgba(43, 0, 56, 0.55)' }}
      >
        <div className="flex-1 text-sm leading-relaxed">
          <p className="mb-1 font-semibold text-moj-gold">
            We value your privacy
          </p>
          <p className="text-moj-cream/90">
            We use cookies to understand how visitors use our site and to
            improve your experience. Analytics cookies are only set with your
            consent. See our{' '}
            <Link
              to="/privacy"
              className="underline underline-offset-2 hover:text-moj-gold"
              data-testid="cookie-consent-privacy-link"
            >
              Privacy Notice
            </Link>{' '}
            for details.
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3">
          <Button
            variant="outline"
            onClick={handleDecline}
            data-testid="cookie-consent-decline-btn"
            className="border-moj-cream/50 bg-transparent text-moj-cream hover:bg-moj-cream/10 hover:text-moj-cream"
          >
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            data-testid="cookie-consent-accept-btn"
            className="bg-moj-gold font-semibold text-moj-purple-deep hover:bg-moj-gold/90"
          >
            Accept cookies
          </Button>
        </div>
      </div>
    </div>
  );
}
